import React, {useState, useEffect, useRef} from "react";
import {Col, Table, Input, Switch, Button, Pagination, Spin} from "antd";
import {useFormik} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {
    SubCompanyContainer,
    FilterContainer,
    MergeOptionsWrapper,
    FilterWrapper,
    SubCompanyTableWrap,
    SubCompanyCardList,
    SubCompanyCard,
    SubCompanyCardTitle,
    SubCompanyCardRow
} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const ReadyOrderNotification = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation();

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData,] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: companyId,
            limit: 10,
            skip: (currentPage - 1) * 10,
            activity: "enabled",
            vehicleType: null,
            city,
            searchText,
        }),
        dependencies: [
            currentPage,
            subCompanyRecall,
            city,
            searchText
        ]
    });

    const formik = useFormik({
        initialValues: {
        options: {
            readyOrderNotification: {
                isActive: companyData?.data?.options?.readyOrderNotification?.isActive || false,
            }
        },
        ...(companyData && companyData.data)
    },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);

            let filters = {};
            filters.searchText = searchText;
            filters.city = city;

            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            if (values.isActive) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);



    const onBulkApplySubmit = async () => {
        setBulkSubmitting(true);
        try {
            const subCompanyIds = subCompanyData?.rows?.map(sc => sc._id) || [];
            const promises = subCompanyIds.map(subCompanyId => {
                return stores.companyStore.updateSubCompanyReadyOrderNotificationSettings({
                    subCompanyId,
                    readyOrder: true
                });
            });

            const results = await Promise.all(promises);
            const allSuccess = results.every(result => result);
            
            if (allSuccess) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
                stores.appStore.showSuccessMessage({
                    content: t("Tüm alt firmalara başarıyla uygulandı")
                });
            }
        } catch (error) {
            console.error('Error updating bulk ready order notification settings:', error);
            stores.appStore.openErrorModal({
                content: t("Toplu uygulama sırasında bir hata oluştu")
            });
        }
        setBulkSubmitting(false);
    };

    const onSwitchChange = async (index, e) => {
        const subCompany = subCompanyData.rows[index];
        setIsSubCompanySubmitting(true);
        try {
            const result = await stores.companyStore.updateSubCompanyReadyOrderNotificationSettings({
                subCompanyId: subCompany._id,
                readyOrder: e
            });
            if (result) {
                setSubCompanyData(prevData => {
                    const newData = {...prevData};
                    const newItem = {
                        ...newData.rows[index],
                        isOrderPreparedActive: e
                    };
                    newData.rows[index] = newItem;
                    return newData;
                });
            }
        } catch (error) {
            stores.appStore.showErrorMessage({
                content: error.message
            });
        } finally {
            setIsSubCompanySubmitting(false);
        }
    };

    return (
        <MergeOptionsWrapper data-testid="readyOrderNotificationScreen">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <FilterWrapper>
                <Col span={12}>
                    <FilterContainer type="flex" gutter={[20, 20]}>
                        <Col span={6}>
                            <CityFilter
                                value={city}
                                onChange={value => {
                                    setCurrentPage(1);
                                    setCity(value);
                                }}
                            />
                        </Col>
                        <Col span={6}>
                            <h5>{t("filters.search", {ns: "common"})}</h5>
                            <Input.Search
                                data-testid="searchInput"
                                value={text}
                                onSearch={value => {
                                    setCurrentPage(1);
                                    setSearchText(value);
                                }}
                                onChange={event => {
                                    setText(event.target.value);
                                }}
                            />
                        </Col>
                    </FilterContainer>
                </Col>
            </FilterWrapper>
            <SubCompanyContainer>
                <Button
                    type="primary"
                    onClick={onBulkApplySubmit}
                    loading={bulkSubmitting}
                    style={{ marginBottom: 16 }}
                    data-testid="bulkApplyButton"
                >
                    {t("Tüm Alt Firmalara Uygula")}
                </Button>
                <SubCompanyTableWrap>
                    <Table
                        rowKey="_id"
                        data-testid="subCompaniesTable"
                        dataSource={subCompanyData?.rows}
                        loading={subCompanyLoading}
                        pagination={{
                            total: subCompanyData.dataCount,
                            current: currentPage,
                            onChange: async page => {
                                setCurrentPage(page);
                                history.push(
                                    "/companies/ready-order-notification-options/" + companyId + "?page=" + page
                                );
                            },
                            hideOnSinglePage: true,
                            showSizeChanger: false
                        }}
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                        <Column
                            dataIndex="name"
                            title={t("fields.subcompany", {ns: "common"})}
                            render={text => <span data-textid="name">{text}</span>}
                        />
                        <Column
                            dataIndex="city"
                            title={t("fields.city", {ns: "common"})}
                            render={text => <span data-textid="city">{text}</span>}
                        />
                        <Column
                            dataIndex="readyOrderNotification"
                            title="Hazır Sipariş Bildirimi"
                            render={(text, record, index) => (
                                <Switch
                                    data-testid="isActiveSwitch"
                                    checked={Boolean(subCompanyData.rows[index]?.isOrderPreparedActive)}
                                    loading={isSubCompanySubmitting}
                                    onChange={e => onSwitchChange(index, e)}
                                />
                            )}
                        />
                    </Table>
                </SubCompanyTableWrap>

                <SubCompanyCardList>
                    {subCompanyLoading ? (
                        <div style={{textAlign: "center", padding: 24}}>
                            <Spin size="large" />
                        </div>
                    ) : !subCompanyData?.rows?.length ? (
                        <EmptyMessage />
                    ) : (
                        <>
                            {(subCompanyData.rows || []).map((record, index) => (
                                <SubCompanyCard key={record._id}>
                                    <SubCompanyCardTitle>
                                        {record.name} · {record.city}
                                    </SubCompanyCardTitle>
                                    <SubCompanyCardRow>
                                        <span className="subcompany-card-label">
                                            Hazır Sipariş Bildirimi
                                        </span>
                                        <Switch
                                            data-testid="isActiveSwitch"
                                            checked={Boolean(record?.isOrderPreparedActive)}
                                            loading={isSubCompanySubmitting}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            onChange={e => onSwitchChange(index, e)}
                                        />
                                    </SubCompanyCardRow>
                                </SubCompanyCard>
                            ))}
                            {subCompanyData?.dataCount > 10 && (
                                <div style={{marginTop: 16, display: "flex", justifyContent: "center"}}>
                                    <Pagination
                                        total={subCompanyData.dataCount}
                                        current={currentPage}
                                        onChange={page => {
                                            setCurrentPage(page);
                                            history.push(
                                                "/companies/ready-order-notification-options/" +
                                                    companyId +
                                                    "?page=" +
                                                    page
                                            );
                                        }}
                                        hideOnSinglePage
                                        showSizeChanger={false}
                                        pageSize={10}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </SubCompanyCardList>
            </SubCompanyContainer>
        </MergeOptionsWrapper>
    );
};